import React, { useRef, useState, useCallback } from 'react';

/*
 * CleanVideo — self-hosted video player (Bunny CDN, no YouTube).
 *
 * Plays an MP4 served from our own Bunny storage zone with a native <video>
 * element: no third-party branding, no cookies, no external player. A poster
 * with our own play button is shown until the viewer clicks, so the video only
 * downloads on demand (better performance) and the page never shows anyone
 * else's chrome.
 *
 * Props:
 *   src    full CDN URL of the MP4, or a bare filename resolved under CDN_BASE
 *   poster optional poster image URL (falls back to a dark placeholder)
 *   title  accessible label
 */

const CDN_BASE = 'https://trailers-mediaverselabs.b-cdn.net/trailers';

function resolveSrc(src) {
  if (/^https?:\/\//i.test(src)) return src;
  const clean = src.replace(/^\/+/, '');
  return `${CDN_BASE}/${clean}`;
}

// By convention a video "name.mp4" has a poster at posters/name.webp.
function defaultPoster(src) {
  const file = src.replace(/^https?:\/\/[^/]+\/trailers\//i, '').replace(/^\/+/, '');
  const base = file.replace(/\.[a-z0-9]+$/i, '');
  if (!base || base.includes('/')) return undefined;
  return `${CDN_BASE}/posters/${base}.webp`;
}

export default function CleanVideo({
  src,
  poster,
  title = 'Video',
  className = '',
}) {
  const [active, setActive] = useState(false);
  const videoRef = useRef(null);
  const url = resolveSrc(src);
  const posterUrl = poster || defaultPoster(src);

  const start = useCallback(() => {
    setActive(true);
    // Play once the element is mounted.
    requestAnimationFrame(() => {
      const v = videoRef.current;
      if (v) {
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay blocked; controls remain */ });
      }
    });
  }, []);

  return (
    <div className={`relative aspect-video overflow-hidden border border-border bg-card ${className}`}>
      {!active && (
        <button
          type="button"
          onClick={start}
          aria-label={`Play ${title}`}
          className="group absolute inset-0 h-full w-full cursor-pointer"
        >
          {posterUrl ? (
            <img
              src={posterUrl}
              alt={title}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <span className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-950" />
          )}
          <span className="absolute inset-0 bg-black/30 transition-colors group-hover:bg-black/20" />
          <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/70 backdrop-blur-sm transition-transform group-hover:scale-110">
            <svg viewBox="0 0 24 24" className="ml-1 h-7 w-7 fill-white">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </button>
      )}

      {active && (
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full bg-black"
          src={url}
          poster={posterUrl}
          controls
          playsInline
          preload="auto"
        >
          <track kind="captions" />
        </video>
      )}
    </div>
  );
}
